import React from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ReactMarkdown from "react-markdown";
import Comments from "./Comments";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    minHeight: "100vh",
    paddingBottom: theme.spacing(6),
    paddingTop: theme.spacing(4),
  },
  contentWrapper: {
    background: "#000000",
    maxWidth: 900,
    margin: "0 auto",
  },
  backButton: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(3),
    color: "white",
  },
  content: {
    maxWidth: 800,
    margin: "0 auto",
    padding: theme.spacing(2),
    color: "white",
    "& h1": {
      fontSize: "2.5rem",
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(3),
    },
    "& h2": {
      fontSize: "2rem",
      marginBottom: theme.spacing(1.5),
      marginTop: theme.spacing(2.5),
    },
    "& h3": {
      fontSize: "1.5rem",
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    "& p": {
      lineHeight: 1.7,
      marginBottom: theme.spacing(1.5),
    },
    "& a": {
      color: "#90caf9",
      textDecoration: "underline",
      "&:hover": {
        color: "#64b5f6",
      },
    },
    "& code": {
      backgroundColor: "#1a1a1a",
      padding: "0.2rem 0.4rem",
      borderRadius: "0.25rem",
      fontFamily: "monospace",
    },
    "& pre": {
      backgroundColor: "#1a1a1a",
      padding: theme.spacing(2),
      borderRadius: "0.5rem",
      overflow: "auto",
      marginBottom: theme.spacing(1.5),
    },
    "& ul, & ol": {
      marginLeft: theme.spacing(3),
      marginBottom: theme.spacing(1.5),
    },
    "& li": {
      marginBottom: theme.spacing(0.5),
    },
  },
}));

const ArticlePage = () => {
  const classes = useStyles();
  const { articleName } = useParams();
  const history = useHistory();
  const [content, setContent] = React.useState("");
  const [title, setTitle] = React.useState("");

  React.useEffect(() => {
    const loadArticle = async () => {
      try {
        // Import all markdown files
        const markdown = require.context("./InfoDump/articles", false, /\.md$/);
        const moduleKey = `./${articleName}.md`;
        
        if (markdown.keys().includes(moduleKey)) {
          const module = markdown(moduleKey);
          const mdPath = module.default || module;
          
          const response = await fetch(mdPath);
          const text = await response.text();
          const cleanedText = text.replace(/<!--[\s\S]*?-->/g, "");
          setContent(cleanedText);
          
          // Convert filename to title: replace -/_ with space and capitalize words
          const displayTitle = articleName
            .replace(/[-_]+/g, " ")
            .split(" ")
            .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
            .join(" ");
          setTitle(displayTitle);
        }
      } catch (error) {
        console.error("Failed to load article:", error);
        setContent("Article not found");
      }
    };

    loadArticle();
  }, [articleName]);

  return (
    <Box component="div" className={classes.mainContainer}>
      <Box className={classes.contentWrapper}>
        <Button
          className={classes.backButton}
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Back
        </Button>

        <Box className={classes.content}>
          <Typography variant="h3" gutterBottom>
            {title}
          </Typography>
          <ReactMarkdown>{content}</ReactMarkdown>
        </Box>

        <Comments articleName={articleName} />
      </Box>
    </Box>
  );
};

export default ArticlePage;
